import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Building2, School, CheckCircle } from 'lucide-react';

const Life = () => {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-white py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-block mb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#DC2626] rounded-full flex items-center justify-center">
                <Heart className="text-white" size={32} fill="white" />
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-[#2C2416] mb-6">
              GUAG/LIFE
            </h1>
            <div className="h-0.5 w-24 bg-[#DC2626] mx-auto mb-8"></div>
            <p className="text-2xl text-[#5C4033] mb-8 leading-relaxed">
              Life-saving awareness at scale.
            </p>
            <p className="text-lg text-[#5C4033] max-w-2xl mx-auto">
              Teaching high school students emergency response skills—CPR, choking intervention, 
              crisis action. Building a generation that doesn't freeze when someone needs help, 
              establishing a culture that values and protects life.
            </p>
          </div>

          {/* Impact Stats */}
          <div className="bg-[#F5F1E8] rounded-2xl p-12 mb-8">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold text-[#DC2626] mb-2">12,400</div>
                <div className="text-sm text-[#5C4033] uppercase tracking-wide">Learners Trained</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-[#DC2626] mb-2">38</div>
                <div className="text-sm text-[#5C4033] uppercase tracking-wide">Schools Reached</div>
              </div>
              <div>
                <div className="text-5xl font-bold text-[#DC2626] mb-2">134</div>
                <div className="text-sm text-[#5C4033] uppercase tracking-wide">Sessions Delivered</div>
              </div>
            </div>
            <div className="text-center mt-8 pt-8 border-t border-[#E5DCC8]">
              <p className="text-sm text-[#8B6F47] uppercase tracking-wider mb-2">Delivered With</p>
              <p className="text-xl font-bold text-[#2C2416]">South African Red Cross</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why It Matters Section */}
      <section className="py-20 bg-[#2C2416] text-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-8">
              Why It Matters
            </h2>
          </div>

          <div className="space-y-8 text-lg text-gray-300 leading-relaxed">
            <p>
              Most people freeze in an emergency. Not because they don't care—because they don't know what to do.
            </p>
            <p>
              <span className="text-white font-semibold">CPR knowledge</span> doesn't just teach a skill. 
              It builds <span className="text-white font-semibold">responsibility</span>. 
              It creates <span className="text-white font-semibold">awareness</span>. 
              It establishes a <span className="text-white font-semibold">culture that protects life</span>.
            </p>
            <p>
              When a student learns how to respond, they become someone who can act. 
              That confidence ripples outward—to their family, their peers, their community.
            </p>
            <blockquote className="border-l-4 border-[#DC2626] pl-6 py-4 my-8">
              <p className="text-xl text-white italic mb-2">
                "The difference between knowing and not knowing is often the difference between life and death."
              </p>
            </blockquote>
            <p className="text-white font-semibold text-xl">
              We're building a generation that doesn't stand by.
            </p>
          </div>
        </div>
      </section>

      {/* The Program Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2C2416] mb-6">
              The Program
            </h2>
            <p className="text-xl text-[#5C4033]">
              Simple. Structured. Scalable.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {/* Session 1 */}
            <div className="bg-[#F5F1E8] rounded-2xl p-8">
              <div className="w-12 h-12 bg-[#DC2626] rounded-full flex items-center justify-center mb-6">
                <span className="text-white text-xl font-bold">1</span>
              </div>
              <h3 className="text-2xl font-bold text-[#2C2416] mb-4">
                CPR Awareness
              </h3>
              <ul className="space-y-2 text-[#5C4033]">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#DC2626] flex-shrink-0 mt-0.5" size={18} />
                  <span>Hands-only CPR technique</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#DC2626] flex-shrink-0 mt-0.5" size={18} />
                  <span>When to act vs when to call for help</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#DC2626] flex-shrink-0 mt-0.5" size={18} />
                  <span>Overcoming hesitation</span>
                </li>
              </ul>
            </div>

            {/* Session 2 */}
            <div className="bg-[#F5F1E8] rounded-2xl p-8">
              <div className="w-12 h-12 bg-[#DC2626] rounded-full flex items-center justify-center mb-6">
                <span className="text-white text-xl font-bold">2</span>
              </div>
              <h3 className="text-2xl font-bold text-[#2C2416] mb-4">
                Choking Response
              </h3>
              <ul className="space-y-2 text-[#5C4033]">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#DC2626] flex-shrink-0 mt-0.5" size={18} />
                  <span>Recognition of airway obstruction</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#DC2626] flex-shrink-0 mt-0.5" size={18} />
                  <span>Back blows and abdominal thrusts</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#DC2626] flex-shrink-0 mt-0.5" size={18} />
                  <span>Infant choking intervention</span>
                </li>
              </ul>
            </div>

            {/* Session 3 */}
            <div className="bg-[#F5F1E8] rounded-2xl p-8">
              <div className="w-12 h-12 bg-[#DC2626] rounded-full flex items-center justify-center mb-6">
                <span className="text-white text-xl font-bold">3</span>
              </div>
              <h3 className="text-2xl font-bold text-[#2C2416] mb-4">
                Emergency Action
              </h3>
              <ul className="space-y-2 text-[#5C4033]">
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#DC2626] flex-shrink-0 mt-0.5" size={18} />
                  <span>Scene safety assessment</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#DC2626] flex-shrink-0 mt-0.5" size={18} />
                  <span>Calling emergency services effectively</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="text-[#DC2626] flex-shrink-0 mt-0.5" size={18} />
                  <span>Recovery position and monitoring</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Life Responder Badge */}
          <div className="mt-16 text-center">
            <div className="inline-block bg-[#2C2416] text-white px-8 py-4 rounded-full">
              <p className="text-sm uppercase tracking-widest">Every participant receives</p>
              <p className="text-xl font-bold mt-1">LIFE RESPONDER</p>
              <p className="text-sm text-gray-400 mt-1">Recognition badge</p>
            </div>
          </div>
        </div>
      </section>

      {/* Real Impact Section */}
      <section className="py-20 bg-[#F5F1E8]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2C2416] mb-6">
              Real Impact
            </h2>
            <p className="text-xl text-[#5C4033]">
              This isn't theoretical. It's already happening.
            </p>
          </div>

          {/* Testimonials */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl p-8">
              <p className="text-lg text-[#5C4033] italic mb-4">
                "A learner from our Grade 11 class used CPR on his younger brother at home. 
                The paramedics said those compressions kept him stable until they arrived. 
                That training saved his brother's life."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#8B6F47] rounded-full"></div>
                <div>
                  <p className="font-semibold text-[#2C2416]">Mr. T. Nkosi</p>
                  <p className="text-sm text-[#8B6F47]">Life Orientation Teacher, Johannesburg</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8">
              <p className="text-lg text-[#5C4033] italic mb-4">
                "Before this program, I would've panicked. Now I know exactly what to do. 
                That confidence doesn't just help me—it helps everyone around me."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#8B6F47] rounded-full"></div>
                <div>
                  <p className="font-semibold text-[#2C2416]">Thandi M.</p>
                  <p className="text-sm text-[#8B6F47]">Grade 12 Learner, Cape Town</p>
                </div>
              </div>
            </div>
          </div>

          {/* Case Example */}
          <div className="mt-12 bg-[#2C2416] text-white rounded-2xl p-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-2 h-2 bg-[#DC2626] rounded-full mt-2"></div>
              <div>
                <p className="text-sm text-gray-400 uppercase tracking-wider mb-2">Case Study</p>
                <h3 className="text-2xl font-bold mb-4">Pretoria High School Intervention</h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  During a school sports day, a student collapsed on the field. Three learners who had completed 
                  GUAG/LIFE immediately began CPR while a teacher called emergency services. The student recovered 
                  fully. The principal later said: <span className="text-white font-semibold">"Those students didn't hesitate. 
                  They acted like trained responders—because they were."</span>
                </p>
                <p className="text-sm text-[#DC2626] font-semibold">
                  Documented intervention — October 2024
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sponsors Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="mb-12">
            <p className="text-sm text-[#8B6F47] uppercase tracking-widest mb-4">
              This program is supported by
            </p>
            <h2 className="text-3xl font-bold text-[#2C2416] mb-8">
              Partners in Life-Saving Education
            </h2>
          </div>

          {/* Placeholder for sponsor logos */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-[#F5F1E8] rounded-xl p-8 flex items-center justify-center h-32">
              <p className="text-[#8B6F47] font-semibold">Lead Partner Logo</p>
            </div>
            <div className="bg-[#F5F1E8] rounded-xl p-8 flex items-center justify-center h-32">
              <p className="text-[#8B6F47] font-semibold">Corporate Sponsor</p>
            </div>
            <div className="bg-[#F5F1E8] rounded-xl p-8 flex items-center justify-center h-32">
              <p className="text-[#8B6F47] font-semibold">Supporting Partner</p>
            </div>
          </div>

          <p className="text-[#5C4033] max-w-2xl mx-auto">
            Corporate partners fund rollouts, provide resources, and help scale this program 
            to every high school in South Africa. Their contribution directly translates to lives saved.
          </p>
        </div>
      </section>

      {/* Proof of Execution */}
      <section className="py-20 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2C2416] mb-6">
              Proof of Execution
            </h2>
            <p className="text-xl text-[#5C4033]">
              We've already done this. At scale.
            </p>
          </div>

          {/* Program in Action - Real Images */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_mirror-web-11/artifacts/mtv4k5pc_CPR%20Teens%20%2B%20GUAG.png"
                alt="GUAG/LIFE Training Session with High School Students"
                className="w-full h-auto"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_mirror-web-11/artifacts/oyymngm9_CPR%20Trainer%20%2B%20GUAG.png"
                alt="Certified Red Cross Trainer with GUAG Program Materials"
                className="w-full h-auto"
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 max-w-3xl mx-auto">
            <p className="text-[#5C4033] text-center leading-relaxed">
              Every session is documented. Every school is tracked. Every learner is registered. 
              This is a national program with clear metrics, verified outcomes, and proven impact.
            </p>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-[#2C2416] text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Get Involved
            </h2>
            <p className="text-xl text-gray-300">
              Two ways to join this movement.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* For Schools */}
            <div className="bg-[#3D3426] rounded-2xl p-10">
              <div className="mb-6">
                <School className="text-[#DC2626]" size={48} />
              </div>
              <h3 className="text-3xl font-bold mb-4">For Schools</h3>
              <p className="text-gray-300 mb-6 text-lg leading-relaxed">
                Bring GUAG/LIFE to your high school. Sessions are delivered by certified trainers 
                from the South African Red Cross. No cost to the school. Just commitment to your learners.
              </p>
              <ul className="space-y-3 mb-8 text-gray-300">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#DC2626] flex-shrink-0 mt-1" size={20} />
                  <span>3-hour program delivered at your school</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#DC2626] flex-shrink-0 mt-1" size={20} />
                  <span>All materials and equipment provided</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#DC2626] flex-shrink-0 mt-1" size={20} />
                  <span>Life Responder badges for every participant</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#DC2626] flex-shrink-0 mt-1" size={20} />
                  <span>Certificate of completion</span>
                </li>
              </ul>
              <Link
                to="/contact"
                className="block w-full px-8 py-4 bg-[#DC2626] text-white rounded-full hover:bg-red-700 transition-colors font-medium text-center text-lg"
              >
                Host a Session
              </Link>
            </div>

            {/* For Corporates */}
            <div className="bg-[#3D3426] rounded-2xl p-10">
              <div className="mb-6">
                <Building2 className="text-[#DC2626]" size={48} />
              </div>
              <h3 className="text-3xl font-bold mb-4">For Corporates</h3>
              <p className="text-gray-300 mb-6 text-lg leading-relaxed">
                Fund a school rollout or sponsor an entire region. Your brand becomes part of a 
                life-saving national movement. Measurable impact. Real social investment.
              </p>
              <ul className="space-y-3 mb-8 text-gray-300">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#DC2626] flex-shrink-0 mt-1" size={20} />
                  <span>Sponsor visibility at every session</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#DC2626] flex-shrink-0 mt-1" size={20} />
                  <span>Detailed impact reporting (schools, learners, regions)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#DC2626] flex-shrink-0 mt-1" size={20} />
                  <span>Social investment alignment with ESG goals</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#DC2626] flex-shrink-0 mt-1" size={20} />
                  <span>Partnership recognition across program materials</span>
                </li>
              </ul>
              <Link
                to="/contact"
                className="block w-full px-8 py-4 bg-white text-[#2C2416] rounded-full hover:bg-gray-100 transition-colors font-medium text-center text-lg"
              >
                Fund a Rollout
              </Link>
            </div>
          </div>

          {/* Bottom Statement */}
          <div className="mt-16 text-center">
            <p className="text-2xl text-gray-300 mb-4">
              Every school reached is another generation equipped.
            </p>
            <p className="text-3xl font-bold">
              Every learner taught validates the mission.
            </p>
          </div>
        </div>
      </section>

      {/* Final Footer CTA */}
      <section className="py-16 bg-[#DC2626] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Don't stand by when someone needs help.
          </h2>
          <p className="text-xl mb-8">
            Be the one who knows what to do.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/contact"
              className="px-10 py-4 bg-white text-[#DC2626] rounded-full hover:bg-gray-100 transition-colors font-bold text-lg"
            >
              Contact Us
            </Link>
            <Link
              to="/"
              className="px-10 py-4 bg-transparent border-2 border-white text-white rounded-full hover:bg-white hover:text-[#DC2626] transition-colors font-bold text-lg"
            >
              About GUAG
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Life;
